import { useAuctionLight, useAuthenticatedUser } from 'contexts'
import { AuctionDTO, AuctionLightDTO } from 'models'
import React from 'react'

/* -------------------------------------------------------------------------- */
/*                                 TYPES                                       */
/* -------------------------------------------------------------------------- */

type ReserveAuthorizationProps = {
  auctionStartDateTime: AuctionDTO['auctionStartDateTime'] | AuctionLightDTO['auctionDate']
  debugOverrideDate?: Date
  debugOverrideNow?: Date
}

// Strict-typed holidays map (no "any")
// type HolidaysMap = Record<string, unknown>

/* -------------------------------------------------------------------------- */
/*                              LIMIT DATE RULES                               */
/* -------------------------------------------------------------------------- */

const limitDates = {
  1: {
    day: 'Lundi',
    limitDays: 3,
    limitHour: 17,
    message: 'Vente le lundi: Modification bloquée le vendredi précédent à 17h00',
  },
  5: {
    day: 'Vendredi',
    limitDays: 1,
    limitHour: 10,
    message: 'Vente le vendredi: Modification bloquée le jeudi précédent à 10h00',
  },
  6: {
    day: 'Samedi',
    limitDays: 2,
    limitHour: 18,
    message: 'Vente le samedi: Modification bloquée jeudi précédent à 18h00',
  },
}

/* -------------------------------------------------------------------------- */
/*                                DATE HELPERS                                 */
/* -------------------------------------------------------------------------- */

const clone = (d: Date) => new Date(d.getTime())

const addDays = (d: Date, n: number) => {
  const nd = clone(d)
  nd.setDate(nd.getDate() + n)
  return nd
}

// const isWeekend = (d: Date) => {
//   const day = d.getDay()
//   return day === 0 || day === 6
// }

// const formatISODate = (d: Date) => d.toISOString().split('T')[0]

// const addOneDay = (d: Date) => addDays(d, 1)

// const isHoliday = (d: Date, holidays: HolidaysMap) => {
//   return Object.prototype.hasOwnProperty.call(holidays, formatISODate(d))
// }

// const isWorkingDay = (d: Date, holidays: HolidaysMap) => {
//   return !isWeekend(d) && !isHoliday(d, holidays)
// }

// const addWorkingDays = (d: Date, days: number, holidays: HolidaysMap) => {
//   let count = 0
//   let date = clone(d)
//   date.setHours(0, 0, 0, 0)

//   // Add N *working* days
//   while (count < days) {
//     date = addOneDay(date)
//     if (isWorkingDay(date, holidays)) {
//       count += 1
//     }
//   }

//   return date
// }

/* -------------------------------------------------------------------------- */
/*                                  HOOK                                       */
/* -------------------------------------------------------------------------- */

const useReserveAuthorization = ({
  auctionStartDateTime,
  debugOverrideDate,
  debugOverrideNow,
}: ReserveAuthorizationProps) => {
  const { auctionLight } = useAuctionLight()
  const { isOnlineAuction } = auctionLight

  // const holidays: HolidaysMap = useHolidays()
  const { isLoginB2C } = useAuthenticatedUser()

  const [modificationMessage, setModificationMessage] = React.useState<string>('')
  const [isModificationRestricted, setIsModificationRestricted] = React.useState<boolean>(true)

  const isAdmin = debugOverrideDate || debugOverrideNow ? false : !isLoginB2C

  const handleRestriction = (restricted: boolean, msg?: string) => {
    setIsModificationRestricted(restricted)
    setModificationMessage(restricted ? msg || '' : '')
  }

  React.useEffect(() => {
    const today = debugOverrideNow ?? new Date()

    if (isAdmin) {
      handleRestriction(false)
      return
    }

    if (!auctionStartDateTime) {
      handleRestriction(true, 'Date de début de vente non définie')
      return
    }

    const saleDate = debugOverrideDate ?? new Date(auctionStartDateTime)

    // Non admin

    if (isOnlineAuction) {
      handleRestriction(false)
      return
    }
    /* ------------------------ ONLINE AUCTIONS: 2H BEFORE ------------------------ */

    // if (isOnlineAuction) {
    //   const diffMs = saleDate.getTime() - today.getTime()
    //   const diffHours = diffMs / (1000 * 60 * 60)

    //   const isRestricted = diffHours < 2
    //   handleRestriction(
    //     isRestricted,
    //     'Modification non autorisée moins de deux heures avant le début de la vente',
    //   )
    //   return
    // }

    /* ------------------------ CUSTOM LIMIT DATE RULES -------------------------- */
    const dayKey = saleDate.getDay() as keyof typeof limitDates
    const limitInfo = limitDates[dayKey]

    if (limitInfo) {
      const limitDate = addDays(saleDate, -limitInfo.limitDays)
      limitDate.setHours(limitInfo.limitHour, 0, 0, 0)

      // Restrict if now is AFTER the allowed limit
      const isRestricted = today.getTime() >= limitDate.getTime()
      handleRestriction(isRestricted, limitInfo.message)
      return
    }

    /* ------------------------ DEFAULT RULE: 1 DAY BEFORE SALE ------------------ */
    const defaultLimit = addDays(saleDate, -1)
    defaultLimit.setHours(0, 0, 0, 0)

    const isRestricted = today.getTime() > defaultLimit.getTime()
    handleRestriction(isRestricted, 'Modification non autorisée le jour de la vente')
  }, [auctionStartDateTime, auctionLight, debugOverrideDate, debugOverrideNow, isAdmin])

  return { isModificationRestricted, modificationMessage }
}

export default useReserveAuthorization
